import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { memo, useEffect, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import CarouselsSkeletonLoader from '@/components/CarouselsSkeletonLoader/CarouselsSkeletonLoader';
import ItemShelf from '@/components/ItemShelf/ItemShelf';
import { RecommendationVisibilityWrapper } from '@/pages/Item/ItemPage/components/ItemPageCarousel/RecommendationVisibilityWrapper';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    fetchRecentItemsIfNeeded,
    getLimitedSortedRecentItemIdsFromState,
    isRecentItemsLoading,
} from '@/redux/modules/recentItem';
import { GlobalState } from '@/redux/store';
import { LotRecommendationType } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { getRecentItemsUrl } from '@/utils/urls';

const MIN_ITEMS_FOR_SEE_ALL = 6;

type Props = {
    className?: string;
};

const RecentItemsCarousel = ({ className }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const itemIds = useAppSelector((state: GlobalState) => getLimitedSortedRecentItemIdsFromState(state, 12));
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const isLoading = useAppSelector(isRecentItemsLoading);
    const [visibleItemIds, setVisibleItemIds] = useState<number[]>([]);

    useEffect(() => {
        if (isLoggedIn) {
            dispatch(fetchRecentItemsIfNeeded());
        }
    }, [dispatch, isLoggedIn]);

    useDeepCompareEffect(() => {
        if (itemIds.length) {
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'RECENT-ITEM-Carousel',
                    itemIds,
                })
            );
        }
    }, [dispatch, itemIds]);

    if (isLoading) {
        return (
            <CarouselsSkeletonLoader
                className={className}
                rows={1}
            />
        );
    }

    if (!itemIds.length) {
        return null;
    }

    return (
        <RecommendationVisibilityWrapper
            recommendationIds={visibleItemIds}
            recommendationType={LotRecommendationType.RecentlyViewed}
        >
            <ItemShelf
                className={className}
                cta={itemIds.length > MIN_ITEMS_FOR_SEE_ALL}
                identifier="recentItems"
                itemIds={itemIds}
                onVisibleItemIdsChange={setVisibleItemIds}
                seeAllUrl={getRecentItemsUrl()}
                testid="recentlyViewedItem"
                title={formatMessage({ id: 'recently_viewed' })}
            />
        </RecommendationVisibilityWrapper>
    );
};

export default memo<Props>(RecentItemsCarousel);
